import { ColumnIndexParticipation, IndexProperties, IndexColumnLinks, PhysicalColumnMetadata } from "./IndexProperties";
import { entityFields, vocabulary } from "../../shared/entityPresentation";
import React, { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  ArrowRightLeft,
  Check,
  ChevronLeft,
  Columns3,
  HelpCircle,
  KeyRound,
  LockKeyhole,
  UnlockKeyhole,
  Pin,
  PinOff,
  Plus,
  Settings2,
  Table2,
  Trash2,
} from "lucide-react";
import type {
  DomainObject,
  ModelDocument,
  Operation,
  Relationship,
  Preferences,
} from "../../shared/contracts";
import { createObject, uid, editFieldType, orderedBusinessKeys } from "../../core";
import { hasPropertyChanges, propertyDraftPatch, propertyDraftValue, updatePropertyDraft, type PropertyDraft } from "./propertyDrafts";
import AttributeGrid from "./AttributeGrid";
import RelationshipIcon from "./RelationshipIcon";
import ModelTypeIcon from "./ModelTypeIcon";
import "./appearance-dialogs.css";
import {
  Button,
  EmptyState,
  Field,
  IconButton,
  Modal,
  Notice,
  modelTypeLabel,
} from "./Primitives";

type Props = {
  document: ModelDocument;
  preferences?: Preferences;
  selection: string[];
  readOnly?: boolean;
  developer?: boolean;
  diagramId?: string;
  diagramReadOnly?: boolean;
  onOperations: (ops: Operation[], summary: string) => void | boolean;
  onSelection: (ids: string[]) => void;
  onAddAttribute: (ownerId: string) => void;
  onAddRelationship: (sourceId?: string) => void;
  onDelete: (id: string) => void;
  onDraftChange?: (dirty: boolean) => void;
  onRegisterSave?: (handler: ((report?: boolean) => boolean) | null) => void;
  onHelp: (id: string) => void;
};
export default function ObjectInspector(p: Props) {
  const object = p.document.objects.find((o) => o.objectId === p.selection[0]);
  const relationship = p.document.relationships.find(
    (o) => o.relationshipId === p.selection[0],
  );
  const [tab, setTab] = useState("general");
  const formRef = useRef<HTMLFormElement>(null);
  const [drafts, setDrafts] = useState<Record<string, PropertyDraft>>({});
  const [gridDirty, setGridDirty] = useState(false);
  const gridSave = useRef<((report?: boolean) => boolean) | null>(null);
  const [keyOpen, setKeyOpen] = useState(false);
  const [geometryError, setGeometryError] = useState("");
  const diagram = p.document.diagrams.find(item => item.diagramId === p.diagramId);
  const selectedRepresentation = object?.representationId || relationship?.representationId || diagram?.representationId;
  const words = vocabulary(p.document, selectedRepresentation, object, selectedRepresentation === diagram?.representationId ? diagram?.perspective : undefined);
  const capitalize = (text: string) => text[0].toUpperCase() + text.slice(1);
  const appearances = object && !object.ownerObjectId ? diagram?.appearances.filter(item => item.objectId === object.objectId) || [] : [];
  const geometryDisabled = p.diagramReadOnly !== false;
  const updateGeometry = (appearanceId: string, changes: { sizeLocked?: boolean; pinned?: boolean; width?: number; height?: number }, summary: string) => {
    if (!diagram || geometryDisabled) return;
    setGeometryError("");
    // Capture the rendered canvas-space dimensions when fixing an automatic box.
    // DOM offset sizes are not affected by the canvas zoom transform.
    if (changes.sizeLocked === true) {
      const node = Array.from(globalThis.document.querySelectorAll<HTMLElement>(".react-flow__node")).find(element => element.dataset.id === appearanceId);
      if (!node || node.offsetWidth <= 0 || node.offsetHeight <= 0) {
        setGeometryError("Show this appearance in the diagram before locking its size.");
        return;
      }
      changes = { ...changes, width: node.offsetWidth, height: node.offsetHeight };
    }
    try { p.onOperations([{ type: "appearance.update", diagramId: diagram.diagramId, appearanceId, changes }], summary); }
    catch (error) { setGeometryError((error as Error).message); }
  };
  const id = object?.objectId || relationship?.relationshipId;
  const selected: any = object || relationship;
  const draft = propertyDraftValue(selected, id ? drafts[id] : undefined);
  const dirty = !!id && hasPropertyChanges(drafts[id]);
  useEffect(() => {
    p.onDraftChange?.(gridDirty || Object.entries(drafts).some(([key,value])=>hasPropertyChanges(value) && (p.document.objects.some(o=>o.objectId===key) || p.document.relationships.some(r=>r.relationshipId===key))));
  }, [drafts, p.document, gridDirty]);
  useEffect(() => { setTab("general"); setGeometryError(""); }, [id]);
  const update = (changes: Record<string, unknown>) =>
    id && setDrafts((prev) => ({ ...prev, [id]: updatePropertyDraft(prev[id], selected, changes) }));
  const commit = (report = true): boolean => {
    if (!dirty) return true;
    if (!id || p.readOnly || !String(draft.name || "").trim()) { if(report) formRef.current?.reportValidity(); return false; }
    if (formRef.current && !formRef.current.checkValidity()) { if(report) formRef.current.reportValidity(); return false; }
    const accepted = p.onOperations(
      [
        {
          type: object ? "object.update" : "relationship.update",
          ...(object ? { objectId: id } : { relationshipId: id }),
          ...propertyDraftPatch(drafts[id]!),
        },
      ],
      `Update ${draft.name}`,
    );
    if (accepted === false) return false;
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    p.onDraftChange?.(false);
    return true;
  };
  const commitAll = (report = true): boolean => {
    if (gridSave.current && !gridSave.current(report)) return false;
    const pending = Object.entries(drafts).flatMap(([draftId, value]) => {
      const original = p.document.objects.find(item => item.objectId === draftId) || p.document.relationships.find(item => item.relationshipId === draftId);
      return original && hasPropertyChanges(value) ? [{draftId,value:propertyDraftValue(original,value),patch:propertyDraftPatch(value)}] : [];
    });
    if (!pending.length) return true;
    if (p.readOnly || p.developer || pending.some(item => !String(item.value.name || "").trim())) { if(report) formRef.current?.reportValidity(); return false; }
    if (formRef.current && !formRef.current.checkValidity()) { if(report) formRef.current.reportValidity(); return false; }
    const operations: Operation[] = pending.map(({draftId,patch}) => p.document.objects.some(item => item.objectId === draftId)
        ? {type:"object.update",objectId:draftId,...patch}
        : {type:"relationship.update",relationshipId:draftId,...patch});
    if(p.onOperations(operations, pending.length === 1 ? `Update ${pending[0]!.value.name}` : `Update ${pending.length} object properties`) === false) return false;
    setDrafts({}); p.onDraftChange?.(false); return true;
  };
  useEffect(() => { p.onRegisterSave?.(commitAll); return () => p.onRegisterSave?.(null); });
  const discard = () =>
    id &&
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  if (!selected)
    return (
      <div className="inspector-content">
        <EmptyState icon={Settings2} title="A place for the details">
          Select a {words.object}, {words.field}, or relationship to inspect its meaning and properties.
        </EmptyState>
        <div className="inspector-model-card">
          <span className="eyebrow">CURRENT MODEL</span>
          <h3>{p.document.model.name}</h3>
          <div className="metadata-row">
            <span>Type</span>
            <strong>{modelTypeLabel(p.document.model.modelType)}</strong>
          </div>
          <div className="metadata-row">
            <span>Objects across representations</span>
            <strong>
              {p.document.objects.filter((o) => !o.ownerObjectId).length}
            </strong>
          </div>
          <div className="metadata-row">
            <span>Relationships</span>
            <strong>{p.document.relationships.length}</strong>
          </div>
          <div className="metadata-row">
            <span>Representations</span>
            <strong>{p.document.representations.length}</strong>
          </div>
          <p className="subtle">
            The model defines meaning. Diagrams are views of the same objects.
          </p>
          <Button icon={HelpCircle} onClick={() => p.onHelp("modeling")}>
            Modeling guide
          </Button>
        </div>
      </div>
    );
  const children = object
    ? p.document.objects.filter((o) => o.ownerObjectId === object.objectId)
    : [];
  const fields = object ? entityFields(p.document, object.objectId).fields : [];
  const allKeys = children.filter((o) => o.kind === "key");
  const keys = [...allKeys.filter(k => k.keyRole !== "business"), ...orderedBusinessKeys(allKeys)];
  const indexes = children.filter((o) => o.kind === "index");
  const isRoot = object && !object.ownerObjectId;
  const isField =
    object &&
    ["attribute", "column", "property", "semanticField", "measure"].includes(
      object.kind,
    );
  const disabled = !!p.readOnly || !!p.developer;
  const parent = object?.ownerObjectId
    ? p.document.objects.find((o) => o.objectId === object.ownerObjectId)
    : null;
  return (
    <div className="inspector-content">
      <div className="inspector-object-head">
        {parent && (
          <button
            className="back-link"
            onClick={() => p.onSelection([parent.objectId])}
          >
            <ChevronLeft size={13} />
            {parent.name}
          </button>
        )}
        <div className="inspector-title">
          <div className="type-icon">
            {relationship ? (
              <RelationshipIcon size={18} />
            ) : isField ? (
              <Columns3 size={18} />
            ) : (
              <ModelTypeIcon type="entity" size={24} />
            )}
          </div>
          <div>
            <span className="eyebrow">
              {relationship ? "RELATIONSHIP" : (isField ? words.field : isRoot ? words.object : object?.kind)?.toUpperCase()}
            </span>
            <h3>{selected.name || "Untitled"}</h3>
          </div>
          <IconButton
            label="Help with properties"
            icon={HelpCircle}
            onClick={() =>
              p.onHelp(relationship ? "relationships" : "properties")
            }
          />
        </div>
        <div className="inspector-tabs">
          <button
            className={tab === "general" ? "active" : ""}
            onClick={() => setTab("general")}
          >
            Properties{dirty && <span className="dirty-dot" />}
          </button>
          {isRoot && (
            <>
              <button
                className={tab === "fields" ? "active" : ""}
                onClick={() => setTab("fields")}
              >
                {capitalize(words.fields)} <span>{fields.length}</span>
              </button>
              <button
                className={tab === "keys" ? "active" : ""}
                onClick={() => setTab("keys")}
              >
                Keys <span>{keys.length}</span>
              </button>
              {words.physical&&<button className={tab === "indexes" ? "active" : ""} onClick={()=>setTab("indexes")}>Indexes <span>{indexes.length}</span></button>}
            </>
          )}
        </div>
      </div>
      <div className="inspector-scroll">
        {p.readOnly && (
          <Notice>These properties are read-only for your role.</Notice>
        )}
        {p.developer && (
          <Notice>
            Developer access allows adding ordinary columns to existing physical
            tables. Other changes require a modeler.
          </Notice>
        )}
        {geometryError && <Notice kind="error">{geometryError}</Notice>}
        {tab === "general" && appearances.length > 0 && <section className="a220-geometry-settings" aria-label="Diagram size and position">
          <h4>Size and position in this diagram</h4>
          <p className="subtle">Size locking and position pinning are independent. Unlocking preserves the current dimensions and position.</p>
          {appearances.map((appearance, index) => {
            const locked = appearance.sizeLocked ?? (appearance.width !== undefined || appearance.height !== undefined);
            return <div className="a220-geometry-row" key={appearance.appearanceId}>
              <span>{appearances.length > 1 ? `Appearance ${index + 1}: ` : ""}{locked ? "Size locked" : "Size unlocked"}{appearance.width !== undefined && appearance.height !== undefined ? ` (${Math.round(appearance.width)} × ${Math.round(appearance.height)})` : ""}; {appearance.pinned ? "position pinned" : "position free"}</span>
              <div><Button type="button" icon={locked ? UnlockKeyhole : LockKeyhole} disabled={geometryDisabled} title={geometryDisabled ? "Diagram editing permission is required" : undefined} onClick={() => updateGeometry(appearance.appearanceId, { sizeLocked: !locked }, `${locked ? "Unlock" : "Lock"} size of ${object!.name}`)}>{locked ? "Unlock size" : "Lock size"}</Button>
              <Button type="button" icon={appearance.pinned ? PinOff : Pin} disabled={geometryDisabled} title={geometryDisabled ? "Diagram editing permission is required" : undefined} onClick={() => updateGeometry(appearance.appearanceId, { pinned: !appearance.pinned }, `${appearance.pinned ? "Unpin" : "Pin"} position of ${object!.name}`)}>{appearance.pinned ? "Unpin position" : "Pin position"}</Button></div>
            </div>;
          })}
        </section>}
        {tab === "general" && (
          <form
            ref={formRef}
            onSubmit={(e) => {
              e.preventDefault();
              commit();
            }}
          >
            <fieldset disabled={disabled}>
              <p className="a210-property-guidance">{relationship ? "Define how these objects relate. Line color and label placement are edited separately in Appearance." : isField ? "Describe this field’s meaning and accepted values. Applying adds a validated change to the model draft." : "These properties belong to the shared model object and appear in every diagram that displays it."}</p>
              <Field label="Name" hint={relationship ? "Use a short verb phrase, for example places or belongs to." : isField ? "Required identifier, for example customer_id. Use the target database’s naming rules for physical columns." : "Required model identifier, for example Customer or sales_order. Renaming keeps the stable identity."}>
                <input
                  value={draft.name || ""}
                  onChange={(e) => update({ name: e.target.value })}
                  required
                />
              </Field>
              {object && (
                <Field
                  label="Business display name"
                  hint="Optional label; does not change the physical identifier."
                >
                  <input
                    value={draft.displayName || ""}
                    onChange={(e) => update({ displayName: e.target.value })}
                    placeholder="A human-readable name"
                  />
                </Field>
              )}
              <Field label="Description" hint="Explain business meaning, scope, and important rules. Plain text is accepted.">
                <textarea
                  value={draft.description || ""}
                  onChange={(e) => update({ description: e.target.value })}
                  rows={4}
                  placeholder="Explain what this represents…"
                />
              </Field>
              {isRoot && <Field label="Brief description" hint="Optional short description for entity diagrams. The full description is used when this is empty."><textarea rows={2} value={draft.briefDescription || ''} onChange={e => update({briefDescription: e.target.value})}/></Field>}
              {isField && !words.conceptual && (
                <>
                  <div className="form-row">
                    <Field label={words.physical ? 'Data type' : 'Logical data type'} hint={words.physical ? 'A type supported by the target database.' : 'Database-independent meaning, for example string(120), integer, decimal, date or boolean.'}>
                      <input list="field-types" value={words.physical ? draft.type?.name || draft.logicalType || '' : draft.logicalType || ''}
                        onChange={e => update(words.physical ? {type: editFieldType(draft.type, {name:e.target.value})} : {logicalType:e.target.value})} placeholder={words.physical ? 'varchar' : 'string'}/>
                      <datalist id="field-types">{(words.physical ? ['varchar','text','integer','bigint','uuid','boolean','date','timestamp','decimal','jsonb'] : ['string','integer','decimal','boolean','date','datetime','identifier','binary']).map(type => <option key={type}>{type}</option>)}</datalist>
                    </Field>
                    {words.physical && <Field label="Length" hint="Positive character limit where applicable."><input type="number" min={1} value={draft.type?.length || ''} onChange={e => update({type:editFieldType(draft.type,{length:e.target.value ? Number(e.target.value) : undefined})})}/></Field>}
                  </div>
                  {words.physical && <Field label="Default expression" hint="A database expression such as CURRENT_TIMESTAMP or a quoted literal."><input value={draft.defaultValue || ''} onChange={e => update({defaultValue:e.target.value})} placeholder="No default"/></Field>}
                  <label className="check-row"><input type="checkbox" checked={draft.nullable !== false} disabled={p.document.objects.some(k => k.kind === "key" && (k.selectedPrimary || k.keyRole === "primary") && k.members?.includes(object!.objectId))} title="Primary-key members must be mandatory / NOT NULL" onChange={e => update({nullable:e.target.checked})}/>Allow null values</label>
                  {words.physical && <label className="check-row"><input type="checkbox" checked={!!draft.generated} onChange={e => update({generated:e.target.checked})}/>Generated value</label>}
                  <p className="subtle">Required fields cannot be null.{words.physical ? ' Generated values are supplied by the database or generation rule.' : ''}</p>
                </>
              )}
              {isRoot && words.physical && (
                <>
                  <Field label="Schema" hint="Optional database namespace, for example public or sales. Blank uses the target’s default.">
                    <input
                      value={draft.schema || ""}
                      onChange={(e) => update({ schema: e.target.value })}
                      placeholder="Default schema"
                    />
                  </Field>
                  <Field label="Physical key strategy" hint="A surrogate identifies a row while the business key retains its uniqueness rule.">
                    <select
                      value={draft.physicalKeyStrategy || "business-primary"}
                      onChange={(e) =>
                        update({ physicalKeyStrategy: e.target.value })
                      }
                    >
                      <option value="business-primary">
                        Business key as primary key
                      </option>
                      <option value="surrogate-primary">
                        Surrogate primary + business key
                      </option>
                    </select>
                  </Field>
                </>
              )}
              {object?.kind === "key" && (
                <>
                  <Field label="Key role" hint="Business keys describe identity; primary keys identify rows; unique keys enforce alternate uniqueness.">
                    <select
                      value={draft.keyRole || "candidate"}
                      onChange={(e) => update({ keyRole: e.target.value })}
                    >
                      {[
                        "primary",
                        "candidate",
                        "business",
                        "surrogate",
                        "unique",
                      ].map((k) => (
                        <option key={k}>{k}</option>
                      ))}
                    </select>
                  </Field>
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={!!draft.selectedPrimary}
                      onChange={(e) =>
                        update({ selectedPrimary: e.target.checked })
                      }
                    />
                    Selected primary key
                  </label>
                  <p className="subtle">
                    Members:{" "}
                    {(draft.members || [])
                      .map(
                        (m: string) =>
                          p.document.objects.find((o) => o.objectId === m)
                            ?.name || m,
                      )
                      .join(", ") || "No members"}
                  </p>
                </>
              )}
              {relationship && (
                <>
                  <div className="section-label">Endpoints & cardinality</div>
                  {(["source", "target"] as const).map((side) => (
                    <div className="endpoint-fields" key={side}>
                      <Field
                        label={`${side === "source" ? "Source" : "Target"} ${words.object}`}
                      >
                        <select
                          value={draft[`${side}ObjectId`]}
                          onChange={(e) =>
                            update({ [`${side}ObjectId`]: e.target.value })
                          }
                        >
                          {p.document.objects
                            .filter(
                              (o) => !o.ownerObjectId && o.kind !== "note" && o.representationId === relationship.representationId,
                            )
                            .map((o) => (
                              <option key={o.objectId} value={o.objectId}>
                                {o.name}
                              </option>
                            ))}
                        </select>
                      </Field>
                      <div className="form-row">
                        <Field label={`${side === "source" ? "Source" : "Target"} cardinality`} hint="The maximum number at this endpoint. Optional allows zero.">
                          <select
                            value={draft[`${side}Cardinality`]}
                            onChange={(e) =>
                              update({ [`${side}Cardinality`]: e.target.value })
                            }
                          >
                            <option value="one">One</option>
                            <option value="many">Many</option>
                          </select>
                        </Field>
                        <label className="check-row">
                          <input
                            type="checkbox"
                            checked={!!draft[`${side}Optional`]}
                            onChange={(e) =>
                              update({ [`${side}Optional`]: e.target.checked })
                            }
                          />
                          {side === "source" ? "Source" : "Target"} optional
                        </label>
                      </div>
                      <Field
                        label={`${side === "source" ? "Source" : "Target"} mapped fields`}
                        hint="Ctrl / Cmd selects multiple fields. Match endpoint field order and compatible types."
                      >
                        <select
                          multiple
                          value={draft[`${side}AttributeIds`] || []}
                          onChange={(e) =>
                            update({
                              [`${side}AttributeIds`]: Array.from(
                                e.target.selectedOptions,
                              ).map((o) => o.value),
                            })
                          }
                        >
                          {p.document.objects
                            .filter(
                              (o) =>
                                o.ownerObjectId === draft[`${side}ObjectId`] &&
                                ["column", "attribute"].includes(o.kind),
                            )
                            .map((o) => (
                              <option key={o.objectId} value={o.objectId}>
                                {o.name}
                              </option>
                            ))}
                        </select>
                      </Field>
                    </div>
                  ))}
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={!!draft.identifying}
                      onChange={(e) =>
                        update({ identifying: e.target.checked })
                      }
                    />
                    Identifying relationship
                  </label>
                  {words.physical && <Field label="On delete" hint="Database action when a referenced row is deleted. CASCADE deletes dependent rows; SET NULL requires nullable fields.">
                    <select
                      value={draft.onDelete || "NO ACTION"}
                      onChange={(e) => update({ onDelete: e.target.value })}
                    >
                      {["NO ACTION", "RESTRICT", "CASCADE", "SET NULL"].map(
                        (s) => (
                          <option key={s}>{s}</option>
                        ),
                      )}
                    </select>
                  </Field>}
                </>
              )}
              {object && (
                <Field label="Lifecycle" hint="Active is in use; deprecated discourages new use; archived retains the definition for reference.">
                  <select
                    value={draft.lifecycle}
                    onChange={(e) => update({ lifecycle: e.target.value })}
                  >
                    {["active", "deprecated", "archived"].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
              )}
              {object?.kind === "index" && <IndexProperties document={p.document} index={draft} onChange={update} onSelect={fieldId=>{if(commit())p.onSelection([fieldId]);}}/>}
              {dirty && (
                <div className="form-actions">
                  <Button onClick={discard} type="button">
                    Discard form
                  </Button>
                  <Button variant="primary" type="submit" icon={Check}>
                    Apply to draft
                  </Button>
                </div>
              )}
            </fieldset>
          </form>
        )}
        {tab === "general" && object?.kind === "index" && <IndexColumnLinks document={p.document} index={object} onSelect={fieldId=>{if(commit())p.onSelection([fieldId]);}}/>}
        {tab === "general" && isField && words.physical && object && <PhysicalColumnMetadata column={object}/>}
        {tab === "general" && isField && words.physical && object && <ColumnIndexParticipation document={p.document} column={object} onSelect={indexId=>{if(commit())p.onSelection([indexId]);}}/>}
        {isRoot && object && <div hidden={tab !== "fields"}>{p.developer && !p.readOnly && object.kind === "table" && <Button icon={Plus} onClick={()=>p.onAddAttribute(object.objectId)}>Add column</Button>}<AttributeGrid key={object.objectId} document={p.document} owner={object} preferences={p.preferences} disabled={disabled} onOperations={p.onOperations} onDirty={setGridDirty} onRegisterSave={handler => {gridSave.current = handler;}} onInspect={id => p.onSelection([id])}/></div>}
        {tab === "keys" && (
          <>
            <div className="section-action">
              <span>Identity & uniqueness</span>
              <Button
                icon={Plus}
                disabled={disabled || !fields.length}
                title={
                  !fields.length
                    ? "Create fields before defining a key"
                    : undefined
                }
                onClick={() => setKeyOpen(true)}
              >
                Add key
              </Button>
            </div>
            <p className="subtle">
              Business identifiers are retained even when a surrogate key is selected. Candidate or unique status alone does not classify a key as a business identifier.
            </p>
            {keys.length === 0 ? (
              <div className="inset-empty">
                No keys defined. Select one or more fields to describe business
                identity.
              </div>
            ) : (
              keys.map((k) => (
                <button
                  key={k.objectId}
                  className="key-list-item"
                  onClick={() => p.onSelection([k.objectId])}
                >
                  <KeyRound size={15} />
                  <span>
                    <strong>{k.name}</strong>
                    <small>
                      {k.keyRole}
                      {k.selectedPrimary ? " · selected primary" : ""}
                    </small>
                    <small>
                      {k.members
                        ?.map(
                          (m) =>
                            p.document.objects.find((o) => o.objectId === m)
                              ?.name,
                        )
                        .join(" + ")}
                    </small>
                  </span>
                  <ArrowUpRight size={13} />
                </button>
              ))
            )}
          </>
        )}
        {tab === "indexes" && object && <section className="index-detail" aria-label="Table indexes"><h4>Physical indexes</h4><p className="subtle">These are physical access structures. Primary, business and unique keys remain on the separate Keys tab.</p>{indexes.map(index=><button className="inspector-list-item" key={index.objectId} type="button" onClick={()=>{if(commit())p.onSelection([index.objectId]);}}>{index.name} · {index.unique?"Unique":"Non-unique"} · {index.members?.length??0} key fields</button>)}{!indexes.length&&<p>No modeled indexes.</p>}<Button icon={Plus} disabled={disabled||!fields.length} onClick={()=>{if(!commit())return;let name=`IX_${object.name}`,number=2;while(children.some(item=>item.name===name))name=`IX_${object.name}_${number++}`;const index=createObject("index",name,object.representationId,object.objectId);index.members=[fields[0].objectId];const dialect=p.document.representations.find(item=>item.representationId===object.representationId)?.targetProfile??p.document.model.targetProfile;index.extensions.engineering={dialect,indexParts:[{fieldId:fields[0].objectId,suffix:"ASC"}]};if(p.onOperations([{type:"object.create",object:index}],`Create ${name}`)!==false)p.onSelection([index.objectId]);}}>Add index</Button></section>}
        <div className="object-meta">
          <span>Stable identity</span>
          <code>{id}</code>
        </div>
        {!disabled && (
          <div className="inspector-bottom-actions">
            {isRoot && (
              <Button icon={RelationshipIcon} onClick={() => p.onAddRelationship(id)}>
                Add relationship
              </Button>
            )}
            <Button
              variant="danger-quiet"
              icon={Trash2}
              onClick={() => p.onDelete(id!)}
            >
              Delete {relationship ? "relationship" : isField ? words.field : isRoot ? words.object : object?.kind}
            </Button>
          </div>
        )}
      </div>
      {keyOpen && object && (
        <KeyDialog
          object={object}
          fields={fields}
          nextBusinessGroup={keys.filter(key=>key.keyRole === "business").length + 1}
          onClose={() => setKeyOpen(false)}
          onCreate={(key) => {
            const selectedPrimary = key.selectedPrimary || key.keyRole === "primary";
            const existingPrimary: Operation[] = selectedPrimary ? keys.filter(k=>k.selectedPrimary || k.keyRole === "primary").map(k=>({type:"object.update",objectId:k.objectId,changes:{selectedPrimary:false,...(k.keyRole === "primary" ? {keyRole:"candidate"} : {})}})) : [];
            const accepted = p.onOperations(
              [...existingPrimary, { type: "object.create", object: {...key, ...(selectedPrimary ? {selectedPrimary:true} : {})} }],
              `Define ${key.name}`,
            );
            if (accepted !== false) setKeyOpen(false);
          }}
        />
      )}
    </div>
  );
}
function KeyDialog({
  object,
  fields,
  nextBusinessGroup,
  onClose,
  onCreate,
}: {
  object: DomainObject;
  fields: DomainObject[];
  nextBusinessGroup: number;
  onClose: () => void;
  onCreate: (key: DomainObject) => void;
}) {
  const [name, setName] = useState(`BK${nextBusinessGroup}`);
  const [role, setRole] = useState<DomainObject["keyRole"]>("business");
  const [members, setMembers] = useState<string[]>([]);
  const [primary, setPrimary] = useState(false);
  return (
    <Modal
      title="Define a key"
      subtitle="Express business identity and uniqueness explicitly."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!name.trim() || !members.length}
            onClick={() =>
              onCreate({
                ...createObject(
                  "key",
                  name,
                  object.representationId,
                  object.objectId,
                ),
                members,
                keyRole: role,
                ...(role === "business" ? {ordinal: nextBusinessGroup - 1} : {}),
                selectedPrimary: primary,
                unique: true,
              })
            }
          >
            Add key to draft
          </Button>
        </>
      }
    >
      <Field label="Key name">
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Purpose">
        <select value={role} onChange={(e) => setRole(e.target.value as any)}>
          {["business", "candidate", "primary", "surrogate", "unique"].map(
            (r) => (
              <option key={r}>{r}</option>
            ),
          )}
        </select>
      </Field>
      <div className="section-label">Members, in selection order</div>
      <div className="choice-list">
        {fields.map((f) => (
          <label className="check-row" key={f.objectId}>
            <input
              type="checkbox"
              checked={members.includes(f.objectId)}
              onChange={(e) =>
                setMembers(
                  e.target.checked
                    ? [...members, f.objectId]
                    : members.filter((m) => m !== f.objectId),
                )
              }
            />
            <span>{f.name}</span>
            {members.includes(f.objectId) && (
              <small>#{members.indexOf(f.objectId) + 1}</small>
            )}
          </label>
        ))}
      </div>
      <label className="check-row">
        <input
          type="checkbox"
          checked={primary}
          onChange={(e) => setPrimary(e.target.checked)}
        />
        Use as the selected primary key
      </label>
      <Notice>
        Composite keys preserve the order selected here. A surrogate does not
        replace the business uniqueness rule.
      </Notice>
    </Modal>
  );
}
